import { z } from 'zod';
/**
 * Scorecard — a weekly table of 5–15 numbers that surface the pulse of
 * the business. Rows ("Measurables") have an owner and a goal; the
 * weekly meeting reviews each row's latest value against goal so misses
 * surface within seven days, not at month-end.
 *
 * Storage layout: one `scorecards/{scorecardId}` doc per org (most orgs
 * have exactly one). Rows live in `measurables/{measurableId}`. Weekly
 * values land in `measurements/{measurementId}` keyed by
 * `(measurableId, weekStart)` — one cell per row × week.
 */
export const SCORECARDS_COLLECTION = 'scorecards';
export const MEASURABLES_COLLECTION = 'measurables';
export const MEASUREMENTS_COLLECTION = 'measurements';
/**
 * Constrained id used in any field that ends up in a Firestore doc id
 * path segment. Firestore rejects `/`, `.` (single), `..`, leading or
 * trailing whitespace, and segments longer than 1500 bytes; passing
 * such a string to `.doc(id)` either throws or — worse — silently
 * routes the write to an unintended subcollection. Members hit
 * scorecard endpoints, so we enforce the constraint at the schema
 * boundary (defense in depth on top of any path-validation Firestore
 * does itself).
 *
 * Length is measured in UTF-8 bytes (not UTF-16 code units) to match
 * Firestore's actual limit — multi-byte characters (emoji, CJK, etc.)
 * still count toward the 1500-byte ceiling. UTF-8 byte length is
 * computed code-unit by code-unit so the schema has zero runtime deps
 * and works identically in Node + the browser (the ECMAScript ES2023
 * lib used here doesn't ship `TextEncoder` types).
 */
const FIRESTORE_DOC_ID_MAX_BYTES = 1500;
/**
 * UTF-8 byte length of a JS string. Iterates the UTF-16 code units once
 * and sums the encoded byte width per code point:
 *   - 1 byte for U+0000–U+007F
 *   - 2 bytes for U+0080–U+07FF
 *   - 3 bytes for U+0800–U+FFFF (skipping a paired low-surrogate)
 *   - 4 bytes for U+10000+ (a high+low surrogate pair → one code point)
 *
 * Stays under the 1500-byte ceiling without allocating a Buffer or
 * needing `TextEncoder`.
 */
function utf8ByteLength(s) {
    let bytes = 0;
    for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 0x80)
            bytes += 1;
        else if (code < 0x800)
            bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff) {
            // High surrogate — consumes the next (low) surrogate as one
            // 4-byte code point.
            bytes += 4;
            i++;
        }
        else
            bytes += 3;
    }
    return bytes;
}
export const FirestoreDocId = z
    .string()
    .min(1)
    .refine((s) => utf8ByteLength(s) <= FIRESTORE_DOC_ID_MAX_BYTES, {
    message: `must be at most ${FIRESTORE_DOC_ID_MAX_BYTES} UTF-8 bytes`,
})
    .refine((s) => !s.includes('/'), {
    message: 'must not contain "/"',
})
    .refine((s) => s !== '.' && s !== '..', {
    message: 'must not be "." or ".."',
})
    .refine((s) => s === s.trim(), {
    message: 'must not have leading or trailing whitespace',
});
/**
 * The Monday or Sunday that anchors the week, formatted as YYYY-MM-DD.
 * Composed into measurement doc ids (`${measurableId}-${weekStart}`)
 * so it must round-trip through a Firestore segment cleanly.
 */
export const WeekStartDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'weekStart must be YYYY-MM-DD');
/**
 * The week-anchor convention. Most teams use Monday; some accounting-
 * driven teams use Sunday. Picked once per scorecard so all measurement
 * `weekStart` values line up across rows.
 */
export const ScorecardWeekStart = z.enum(['monday', 'sunday']);
export const Scorecard = z.object({
    id: FirestoreDocId,
    orgId: z.string().min(1),
    title: z.string().min(1),
    weekStart: ScorecardWeekStart,
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * The goal expression. `>=` / `<=` / `==` cover the typical thresholds;
 * `between` uses `value` as the lower bound and `secondaryValue` as the
 * upper bound (inclusive on both ends).
 */
export const MeasurableGoal = z.object({
    op: z.enum(['>=', '<=', '==', 'between']),
    value: z.number(),
    /** Upper bound when `op === 'between'`; ignored otherwise. */
    secondaryValue: z.number().optional(),
    /** Free-form unit string for display ("$", "%", "calls", "NPS"). */
    unit: z.string().optional(),
});
/**
 * One row on the scorecard. Sort order is explicit (`sortIndex`) so
 * drag-reordering doesn't depend on createdAt timestamps.
 *
 * Archive lifecycle: `archivedAt` (ISO timestamp) flips the row into
 * an inert state — the row stays queryable for forensics + sparkline
 * history rendering, but the read path short-circuits its derived
 * `status` to `'idle'` so the goal-vs-actual color band stops painting
 * red. A truly destructive remove is `deleteMeasurable`.
 */
export const Measurable = z.object({
    id: FirestoreDocId,
    orgId: z.string().min(1),
    scorecardId: FirestoreDocId,
    title: z.string().min(1),
    /** The single accountable owner — the person whose number this is. */
    ownerUid: z.string().min(1),
    goal: MeasurableGoal,
    sortIndex: z.number().int().nonnegative(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * ISO timestamp set by `archiveMeasurable` when the row is archived.
     * Optional — most rows are active. The read path treats archived rows
     * as `status: 'idle'` regardless of the latest measurement so the
     * row stops being a live signal once the team retires it.
     */
    archivedAt: z.string().optional(),
});
/** One cell — the value reported for a measurable in a specific week. */
export const Measurement = z.object({
    id: FirestoreDocId,
    orgId: z.string().min(1),
    measurableId: FirestoreDocId,
    /**
     * ISO date string (YYYY-MM-DD) for the Monday or Sunday that anchors
     * the week — matches the parent scorecard's `weekStart` convention.
     */
    weekStart: WeekStartDate,
    value: z.number(),
    /** Uid of the person who entered the number (often, but not always, the owner). */
    capturedByUid: z.string().min(1),
    capturedAt: z.string(),
});
/** Body for `POST /api/scorecards/:scorecardId/measurables`. */
export const CreateMeasurableRequest = z.object({
    scorecardId: FirestoreDocId,
    title: z.string().min(1),
    ownerUid: z.string().min(1),
    goal: MeasurableGoal,
    sortIndex: z.number().int().nonnegative(),
});
/** Body for `PATCH /api/measurables/:id`. */
export const UpdateMeasurableRequest = z.object({
    title: z.string().min(1).optional(),
    ownerUid: z.string().min(1).optional(),
    goal: MeasurableGoal.optional(),
    sortIndex: z.number().int().nonnegative().optional(),
});
/** Body for `POST /api/measurables/:id/measurements`. */
export const CreateMeasurementRequest = z.object({
    measurableId: FirestoreDocId,
    weekStart: WeekStartDate,
    value: z.number(),
});
/**
 * Compute the signed delta between a value and a goal. Returns `null`
 * when the goal is `'=='` (delta-as-distance is less meaningful than
 * the equality check itself; the UI surfaces `'on'`/`'off'` instead).
 *
 * Sign convention is "positive = on the right side of the goal":
 *   - `>=` goal of 100, value 120 → `+20`
 *   - `<=` goal of  10, value   7 → `+3`  (lower is better; below = positive)
 *   - `between [10, 20]`, value 15 → distance to nearest bound (`+5`)
 *   - `between [10, 20]`, value 25 → `-5` (outside the band on the high side)
 */
export function computeMeasurableDelta(value, goal) {
    if (goal.op === '==')
        return null;
    if (goal.op === '>=')
        return value - goal.value;
    if (goal.op === '<=')
        return goal.value - value;
    // 'between' — secondaryValue is the upper bound; value defaults to it
    // if the seeder forgot to set it (tolerant: collapses to an equality).
    const lo = goal.value;
    const hi = goal.secondaryValue ?? goal.value;
    if (value >= lo && value <= hi) {
        // Inside the band; positive distance to the nearest edge.
        return Math.min(value - lo, hi - value);
    }
    // Outside the band; negative distance to the nearest edge.
    return value < lo ? value - lo : hi - value;
}
/**
 * Derive the `'on' | 'watch' | 'off'` health pill from a value + goal.
 * `watchTolerance` is the share of the goal threshold that still counts
 * as `'watch'` (default 10%). Negatives below that → `'off'`; zero or
 * positive → `'on'`. `'=='` is a strict-equality test.
 *
 * Returns `'idle'` when `value` is `null` (no measurement on file yet) —
 * the UI primitive renders that as a muted dot.
 */
export function deriveMeasurableStatus(value, goal, opts = {}) {
    if (value === null)
        return 'idle';
    if (goal.op === '==') {
        return value === goal.value ? 'on' : 'off';
    }
    const delta = computeMeasurableDelta(value, goal);
    if (delta === null)
        return 'idle';
    if (delta >= 0)
        return 'on';
    // Watch band: within `watchTolerance` (default 10%) of the goal on the
    // wrong side. Goal of 0 collapses to a strict on/off split since `0 *
    // any tolerance` is `0`.
    const tol = opts.watchTolerance ?? 0.1;
    const reference = goal.op === 'between'
        ? Math.max(Math.abs(goal.value), Math.abs(goal.secondaryValue ?? goal.value))
        : Math.abs(goal.value);
    const watchBand = reference * tol;
    return Math.abs(delta) <= watchBand ? 'watch' : 'off';
}
/** Body for `POST /api/orgs/:o/scorecards`. */
export const CreateScorecardRequest = z.object({
    id: FirestoreDocId,
    title: z.string().min(1),
    weekStart: ScorecardWeekStart,
});
/**
 * Derived health for one measurable, computed on the read path so the
 * UI doesn't re-implement goal arithmetic. `'on'` → at-or-better-than
 * goal, `'watch'` → within 10% of goal on the wrong side, `'off'` →
 * worse than that. `'idle'` → no measurement on file yet (sparkline
 * renders the dash).
 *
 * Maps cleanly onto `<Status kind=…>` in the UI primitive.
 */
export const MeasurableStatus = z.enum(['on', 'watch', 'off', 'idle']);
/**
 * One row in the read-path response — the persisted `Measurable` plus
 * the derived `latest`/`delta`/`status`/`spark` fields the Today + Scorecard
 * surfaces consume directly.
 */
export const ScorecardRow = Measurable.extend({
    /** The most-recent measurement (by `weekStart`), if any has been recorded. */
    latest: Measurement.nullable(),
    /**
     * Signed difference between the latest value and the goal threshold.
     * For `op: '>='` and `'<='` it's `value - threshold` (positive = above);
     * for `'between'` it's distance to the nearest bound (negative when
     * outside the range, positive when inside). `null` when no measurement
     * yet exists or the goal is `'=='` (where delta-as-distance is less
     * meaningful than the equality check itself).
     */
    delta: z.number().nullable(),
    /**
     * Last-N values (oldest → newest) for the sparkline. Length is bounded
     * server-side (default 8) so the wire payload stays predictable.
     */
    spark: z.array(z.number()),
    /** Derived goal-vs-actual health. See `MeasurableStatus`. */
    status: MeasurableStatus,
});
/**
 * Shape returned by `GET /api/orgs/:o/scorecards/:id`. Bundles the
 * scorecard meta + the rows (with derived health) + the raw measurements
 * sliced to the requested window so a drill-in modal can render the
 * full 13-week table without a second round-trip.
 */
export const ReadScorecardResponse = z.object({
    scorecard: Scorecard,
    rows: z.array(ScorecardRow),
    /** All measurements covered by the read window, recent-first. */
    measurements: z.array(Measurement),
});
